/**
 * La carte d'activité commerciale ou artisanale ambulante, et son imprimé.
 *
 * Deuxième vrai Cerfa de la rubrique après le 1447-C : on ne compose pas une
 * lettre, on REMPLIT le 14022*02, « déclaration préalable d'une activité
 * commerciale ou artisanale ambulante », qui se dépose à la chambre de
 * commerce ou à la chambre des métiers.
 *
 * LE TRI EST LE CŒUR DE L'OUTIL, parce que la carte est réclamée à des gens
 * qui n'en ont pas besoin et ignorée par ceux qui la doivent. L'article
 * L. 123-29 du code de commerce vise l'activité exercée « hors du territoire
 * de la commune où est situé son habitation ou son principal établissement » :
 * vendre toute l'année sur le marché de sa propre commune n'appelle aucune
 * carte. Son second alinéa ne laisse pas cette porte à « toute personne sans
 * domicile stable », qui déclare où qu'elle exerce. L'article R. 123-208-1
 * écarte en plus plusieurs métiers et les tournées accessoires dans les
 * communes limitrophes. On a longtemps écrit ici « cinq professions » : c'est
 * faux, le 4° vise tout démarchage encadré par un texte particulier et ne cite
 * que « notamment » les VRP, le démarchage bancaire ou financier et les
 * vendeurs à domicile indépendants.
 *
 * L'IMPRIMÉ EST UN FORMULAIRE XFA HYBRIDE : pdf-lib retire sa couche XFA au
 * chargement, et c'est l'AcroForm classique qui reçoit les valeurs. Les noms de
 * champs sont en partie opaques (`Champ_de_texte4[0]` pour la date de
 * naissance), et deux jeux de contrôles se superposent en haut de page, trois
 * cases à cocher et un groupe de boutons radio dont les trois options portent
 * la même valeur. Ce sont les CASES qui sont posées, jamais le groupe : ses
 * options homonymes ne se distinguent pas.
 *
 * PIÈGE PAYÉ UNE FOIS : le champ nommé `signature[0]` n'est pas la signature,
 * c'est le « Fait à » du bas de page. La signature elle-même n'a pas de champ,
 * et se dessine sous la mention « Signature, ».
 */

import { dateEnToutesLettres, identite, nettoyer, nomDeFamille } from "./documentText.js";

/** L'imprimé servi au navigateur, et ce qui l'identifie. */
export const MILLESIME = {
    numero: "14022*02",
    fichier: "/formulaires/declaration-ambulante-14022-02.pdf",
};

/** Ce que la carte coûte, redevance fixée par arrêté du 19 juillet 2019. */
export const COUT_EUROS = 30;

/** Sa durée de validité, article R. 123-208-4 du code de commerce. */
export const VALIDITE_ANS = 4;

/**
 * La fenêtre de renouvellement, en mois autour de l'échéance.
 *
 * Un mois avant, deux mois après : passé ce délai, ce n'est plus un
 * renouvellement mais une demande initiale, et les pièces ne sont pas les
 * mêmes. C'est la seule règle de l'outil qui décide à la place du lecteur.
 */
export const RENOUVELLEMENT_AVANT_MOIS = 1;
export const RENOUVELLEMENT_APRES_MOIS = 2;

/**
 * Les trois situations du tri géographique.
 *
 * `sansDomicileStable` n'est pas un détail : le second alinéa de L. 123-29 y
 * attache la déclaration où que l'activité s'exerce, même dans une seule
 * commune.
 */
export const ZONES = ["maCommune", "horsCommune", "sansDomicileStable"];

/**
 * Ce que l'article R. 123-208-1 écarte de la déclaration.
 *
 * Son I laisse à leur propre réglementation les agents commerciaux, les
 * vendeurs-colporteurs de presse, les exploitants de taxis et les démarcheurs
 * qu'encadre un texte particulier, dont il cite « notamment » les VRP, le
 * démarchage bancaire ou financier et les vendeurs à domicile indépendants.
 * Son II écarte les tournées accessoires dans les communes limitrophes, faites
 * depuis un établissement fixe.
 *
 * Elles sont énumérées ici parce que le parcours les propose une par une : une
 * dispense qu'on ne nomme pas est une dispense que personne ne réclame.
 */
export const DISPENSES = [
    "agentCommercial",
    "colporteurPresse",
    "taxi",
    "vrp",
    "demarchageFinancier",
    "vendeurADomicile",
    "tourneesLimitrophes",
];

/** Une date ISO découpée, ou null. */
function jour(iso) {
    const t = nettoyer(iso);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return null;
    const [a, m, j] = t.split("-").map(Number);
    const d = new Date(Date.UTC(a, m - 1, j));
    if (d.getUTCFullYear() !== a || d.getUTCMonth() !== m - 1 || d.getUTCDate() !== j) return null;
    return { a, m, j, date: d };
}

/** Décale une date d'un nombre de mois, en reculant au dernier jour du mois. */
function decaler(iso, mois) {
    const d = jour(iso);
    if (!d) return null;
    const cible = new Date(Date.UTC(d.a, d.m - 1 + mois, d.j));
    if (cible.getUTCDate() !== d.j) cible.setUTCDate(0);
    return cible.toISOString().slice(0, 10);
}

/**
 * Où en est une carte par rapport à sa date d'expiration.
 *
 * Trois états, et un seul autorise le renouvellement : trop tôt, dans la
 * fenêtre, ou trop tard, auquel cas il faut repartir sur une demande initiale.
 */
export function fenetreDeRenouvellement(expiration, aujourdhui) {
    const fin = jour(expiration);
    const maintenant = jour(String(aujourdhui).slice(0, 10));
    if (!fin || !maintenant) return null;

    const ouverture = decaler(expiration, -RENOUVELLEMENT_AVANT_MOIS);
    const fermeture = decaler(expiration, RENOUVELLEMENT_APRES_MOIS);
    const avant = maintenant.date < jour(ouverture).date;
    const apres = maintenant.date > jour(fermeture).date;

    return {
        ouverture,
        fermeture,
        etat: avant ? "tropTot" : apres ? "tropTard" : "ouverte",
    };
}

/** La date d'expiration d'une carte délivrée à telle date. */
export function expirationDe(dateDelivrance) {
    return decaler(dateDelivrance, VALIDITE_ANS * 12);
}

/**
 * Qui doit déclarer, et qui n'a rien à déclarer.
 *
 * Deux portes fermées avant toute saisie : l'activité qui ne sort pas de sa
 * commune, pour qui a un domicile stable, et ce que le règlement écarte. Sans
 * domicile stable, la première porte n'existe pas et seules les dispenses
 * jouent. Une réponse inconnue ne tranche rien : prise pour une dispense, elle
 * écarterait quelqu'un qui doit la carte.
 */
export function concerne(reponses) {
    const r = reponses || {};
    if (!ZONES.includes(r.zone)) return { concerne: null, motif: null };
    if (r.zone === "maCommune") return { concerne: false, motif: "dansMaCommune" };
    if (r.dispense !== "aucune" && !DISPENSES.includes(r.dispense)) {
        return { concerne: null, motif: null };
    }
    if (r.dispense !== "aucune") return { concerne: false, motif: "dispense" };
    if (!r.objet) return { concerne: null, motif: null };
    if (r.objet === "renouvellement") {
        const fenetre = fenetreDeRenouvellement(r.expiration, r.aujourdhui);
        if (fenetre?.etat === "tropTard") return { concerne: false, motif: "renouvellementTropTard" };
    }
    return { concerne: true, motif: null };
}

/** Ce qui manque, ce qui est refusé, et ce qui mérite un mot. */
export function valider(reponses, aujourdhui) {
    const r = { ...(reponses || {}), aujourdhui };
    const manques = [];
    const invalides = [];
    const alertes = [];

    if (!concerne(r).concerne) {
        return { valide: false, manques: ["zone"], invalides, alertes };
    }

    const requis = [
        ["nomNaissance", r.nomNaissance],
        ["prenoms", r.prenoms],
        ["nationalite", r.nationalite],
        ["domicile", r.domicile],
        ["activite", r.activite],
        ["lieuNaissance", r.lieuNaissance],
        ["lieuDeSignature", r.lieuDeSignature],
    ];
    for (const [cle, valeur] of requis) {
        if (!nettoyer(valeur)) manques.push(cle);
    }

    for (const cle of ["dateNaissance", "dateDeSignature"]) {
        const brut = nettoyer(r[cle]);
        if (!brut) manques.push(cle);
        else if (!jour(brut)) invalides.push({ champ: cle, motif: "format" });
    }

    /* Une date de naissance dans le futur, ou qui ferait un mineur, n'est pas
       une coquille anodine : elle part telle quelle à la chambre. */
    const naissance = jour(r.dateNaissance);
    const maintenant = jour(String(aujourdhui).slice(0, 10));
    if (naissance && maintenant) {
        const ans = (maintenant.date - naissance.date) / (365.25 * 86400000);
        if (ans < 0) invalides.push({ champ: "dateNaissance", motif: "aVenir" });
        else if (ans < 16) invalides.push({ champ: "dateNaissance", motif: "tropJeune" });
        else if (ans > 110) invalides.push({ champ: "dateNaissance", motif: "tropVieux" });
    }

    if (r.objet === "renouvellement") {
        if (!nettoyer(r.expiration)) manques.push("expiration");
        else if (!jour(r.expiration)) invalides.push({ champ: "expiration", motif: "format" });
        else if (fenetreDeRenouvellement(r.expiration, aujourdhui)?.etat === "tropTot") {
            alertes.push("renouvellementTropTot");
        }
    }

    // La demande faite en même temps que la création d'entreprise n'exige que
    // deux photographies : le dire évite de courir après des pièces inutiles.
    if (r.objet === "initiale" && r.avecCreation === "oui") alertes.push("piecesAllegees");

    return {
        valide: manques.length === 0 && invalides.length === 0,
        manques: [...new Set(manques)],
        invalides,
        alertes,
    };
}

/**
 * La date de naissance telle que l'imprimé l'attend, en chiffres.
 *
 * Le Cerfa demande « Date et lieu de naissance » sur une seule ligne, coupée en
 * deux champs : la date reste en chiffres, le lieu suit dans le champ voisin.
 */
export function dateCourte(iso) {
    const d = jour(iso);
    if (!d) return "";
    return `${String(d.j).padStart(2, "0")}/${String(d.m).padStart(2, "0")}/${d.a}`;
}

/** Le nom des champs de l'imprimé, préfixe XFA compris. */
const CHAMP = (nom) => `topmostSubform[0].Page1[0].${nom}`;

/**
 * Ce qu'on écrit dans l'imprimé, champ par champ.
 *
 * Seul le cadre « personnes physiques » est rempli : une micro-entreprise n'est
 * pas une personne morale, et laisser le second cadre vide est ce que la
 * chambre attend, pas un oubli.
 */
export function composerChamps(reponses, aujourdhui) {
    const r = reponses || {};
    const dateDeSignature = nettoyer(r.dateDeSignature) || String(aujourdhui).slice(0, 10);
    const complet = { ...r, dateDeSignature };
    if (!valider(complet, aujourdhui).valide) return null;

    /* Chaque objet a sa case, et le rectangle de cette case, relevé une fois
       dans l'imprimé : la valeur du champ ne suffit pas à la faire voir. */
    const CASES = {
        initiale: { nom: "Case_à_cocher4[0]", x: 237.78, y: 695.32 },
        renouvellement: { nom: "Case_à_cocher2[0]", x: 237.78, y: 677.38 },
        modification: { nom: "Case_à_cocher3[0]", x: 237.78, y: 659.65 },
    };
    const caseDeLObjet = CASES[r.objet];

    return {
        textes: {
            [CHAMP("NomNaissance[0]")]: nomDeFamille(r.nomNaissance),
            [CHAMP("prénomNaissance[0]")]: nettoyer(r.prenoms),
            [CHAMP("Nomdusage[0]")]: r.nomUsage ? nomDeFamille(r.nomUsage) : "",
            [CHAMP("Champ_de_texte4[0]")]: dateCourte(r.dateNaissance),
            [CHAMP("Champ_de_texte2[0]")]: nettoyer(r.lieuNaissance),
            [CHAMP("Nationalité[0]")]: nettoyer(r.nationalite),
            [CHAMP("Domicile[0]")]: nettoyer(r.domicile),
            [CHAMP("ActivitéCommerciale[0]")]: nettoyer(r.activite),
            // Le champ nommé « signature » porte le lieu, pas le paraphe.
            [CHAMP("signature[0]")]: nettoyer(r.lieuDeSignature),
            [CHAMP("Champ_de_texte6[0]")]: dateCourte(dateDeSignature),
        },
        cases: [CHAMP(caseDeLObjet.nom)],
        choix: {},
        radios: {},
        marques: [{ page: 1, x: caseDeLObjet.x, y: caseDeLObjet.y, cote: 10 }],
        /* Sous la mention « Signature, » du bas de page, dont la ligne de base
           est à 56 points : le paraphe se pose en dessous, dans les deux
           centimètres qui restent. */
        signature: { page: 1, x: 292, y: 14, largeur: 150, hauteur: 36 },
        millesime: MILLESIME,
    };
}

/** Ce que le parcours annonce avant le téléchargement. */
export function recapitulatif(reponses, aujourdhui) {
    const r = reponses || {};
    if (!composerChamps(r, aujourdhui)) return null;

    const objet = {
        initiale: "Demande initiale",
        renouvellement: "Renouvellement",
        modification: "Modification",
    }[r.objet];

    const lignes = [
        ["Objet de la déclaration", objet],
        ["Déclarant", identite({ prenoms: r.prenoms, nom: r.nomNaissance })],
        ["Activité déclarée", nettoyer(r.activite)],
        ["Commune de rattachement", nettoyer(r.domicile)],
        ["Redevance", `${COUT_EUROS} €, à régler à la chambre`],
    ];

    if (r.objet === "renouvellement" && jour(r.expiration)) {
        lignes.splice(1, 0, ["Carte à renouveler avant le", dateEnToutesLettres(r.expiration)]);
    }
    return lignes;
}
